import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Card from "#/components/shared/card";
import Input from "#/components/shared/input";
import Typography from "#/components/shared/typography";
import { ActionButton } from "#/components/shared/buttons/ActionButtons";
import { formatDate } from "#/components/utls/time";
import useArticlesList from "#/admin/articles/hooks/useArticlesList";
import { ArticleListItem } from "#/services/articles";

const statusClasses: Record<string, string> = {
  published: "bg-emerald-100 text-emerald-700",
  draft: "bg-amber-100 text-amber-700",
  archived: "bg-slate-100 text-slate-600",
};

function formatStatus(status?: string | null) {
  if (!status) return "Unknown";
  return status
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export function ArticlesList() {
  const navigate = useNavigate();
  const listQuery = useArticlesList();
  const [search, setSearch] = useState("");

  const articles = useMemo(() => {
    const items = Array.isArray(listQuery.data) ? listQuery.data : [];
    const trimmed = search.trim().toLowerCase();
    const filtered = trimmed
      ? items.filter((item) =>
          [item.title, item.slug]
            .filter(Boolean)
            .join(" ")
            .toLowerCase()
            .includes(trimmed)
        )
      : items;

    return [...filtered].sort((a, b) => {
      const aDate = new Date(a.updatedAt || a.createdAt || 0).getTime();
      const bDate = new Date(b.updatedAt || b.createdAt || 0).getTime();
      return bDate - aDate;
    }) as ArticleListItem[];
  }, [listQuery.data, search]);

  return (
    <div className="flex flex-col gap-4">
      <Card className="w-full" variant="default" elevation={1}>
        <Card.Header divider>
          <div className="flex flex-col gap-1">
            <Typography variant="h3" className="text-brown-900">
              Articles
            </Typography>
            <Typography variant="body2" className="text-brown-600">
              Review published and draft articles.
            </Typography>
          </div>
        </Card.Header>
        <Card.Body className="space-y-4 bg-white">
          <Input
            id="article-search"
            label="Search"
            placeholder="Search by title or slug"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            fullWidth
          />

          {listQuery.isLoading ? (
            <Typography variant="body2" className="text-brown-600">
              Loading articles…
            </Typography>
          ) : listQuery.isError ? (
            <div className="rounded-lg border border-terracotta/30 bg-cream-50 p-4">
              <Typography variant="body2" className="text-terracotta">
                {listQuery.error instanceof Error
                  ? listQuery.error.message
                  : "Unable to load articles."}
              </Typography>
              <div className="mt-3">
                <ActionButton size="sm" onClick={() => listQuery.refetch()}>
                  Retry
                </ActionButton>
              </div>
            </div>
          ) : articles.length === 0 ? (
            <Typography variant="body2" className="text-brown-600">
              No articles found.
            </Typography>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="bg-cream-50 text-brown-700">
                  <tr>
                    <th className="px-4 py-3 font-semibold">Title</th>
                    <th className="px-4 py-3 font-semibold">Slug</th>
                    <th className="px-4 py-3 font-semibold">Version</th>
                    <th className="px-4 py-3 font-semibold">Status</th>
                    <th className="px-4 py-3 font-semibold">Updated</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-beige-100">
                  {articles.map((article) => {
                    const statusKey = article.status?.toLowerCase() ?? "unknown";
                    const statusClass =
                      statusClasses[statusKey] ?? "bg-beige-100 text-brown-700";
                    return (
                      <tr
                        key={article.id}
                        className="cursor-pointer hover:bg-cream-50"
                        onClick={() =>
                          navigate(`/admin/content/articles/${article.slug}`)
                        }
                      >
                        <td className="px-4 py-3 text-brown-900">
                          {article.title}
                        </td>
                        <td className="px-4 py-3 text-brown-700">
                          {article.slug}
                        </td>
                        <td className="px-4 py-3 text-brown-700">
                          v{article.version}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold ${statusClass}`}
                          >
                            {formatStatus(article.status)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-brown-700">
                          {article.updatedAt || article.createdAt
                            ? formatDate(article.updatedAt || article.createdAt || "")
                            : "Unknown"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
}

export default ArticlesList;
